import React, { useState, useEffect } from 'react';
import { Button, Badge, Form } from 'react-bootstrap';
import Select from 'react-select';
import { FaTrash, FaUser, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';

const STORE_API_URL = process.env.REACT_APP_API_URL;

// Role options for contributors
const CONTRIBUTION_ROLES = [
  { value: 'author', label: 'Author', color: 'primary' },
  { value: 'co-author', label: 'Co-Author', color: 'info' },
  { value: 'contributor', label: 'Contributor', color: 'secondary' },
  { value: 'editor', label: 'Editor', color: 'success' }
];

const AuthorInput = ({ 
  authors, // array of { user_id?, name?, role, url, notes } objects
  onChange, 
  disabled,
  token
}) => {
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState(CONTRIBUTION_ROLES[0]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualName, setManualName] = useState('');

  // Fetch available users on mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${STORE_API_URL}/users/authors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch users');
      
      const { authors: fetchedUsers } = await response.json();
      // Store users with string IDs for UI handling
      setAvailableUsers(fetchedUsers.map(user => ({
        ...user,
        _id: user._id.toString() // Ensure ID is string for consistent comparison
      })));
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load available users');
    }
  };

  const removeAuthor = (index) => {
    const newAuthors = [...authors];
    newAuthors.splice(index, 1);
    onChange(newAuthors);
  };

  // Helper function to get user details
  const getUserDetails = (userId) => {
    return availableUsers.find(u => u._id === userId);
  };

  // Helper function to get display name
  const getDisplayName = (author) => {
    if (author.author_name) return author.author_name; 
    if (!author.user_id) return author.author_name || 'Unknown User';
    const user = getUserDetails(author.user_id);
    return user ? (user.author_name || user.display_name || 'Unnamed User') : 'Unknown User';
  };

  const handleAddAuthor = () => {
    if (showManualInput) {
      // Add manual author
      if (!manualName.trim()) {
        toast.error('Please enter an author name');
        return;
      }
      onChange([
        ...authors,
        {
          author_name: manualName.trim(),
          role: selectedRole.value,
          url: '',
          notes: ''
        }
      ]);
      setManualName('');
      setShowManualInput(false);
    } else if (selectedUser) {
      // Add user-linked author
      onChange([
        ...authors,
        {
          user_id: selectedUser.value,
          role: selectedRole.value,
          url: '',
          notes: ''
        }
      ]);
      setSelectedUser(null);
    } else {
      toast.error('Please select a user or enter a name');
    }
  };

  return (
    <div className="author-input-container" style={{ width: '100%' }}>
      <h6 className="mb-3" style={{ borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>Credits & Attribution</h6>
      
      {/* Add Author Section */}
      <div className="d-flex gap-2 mb-3 flex-wrap">
        {!showManualInput ? (
          <>
            <div style={{ flex: 2, minWidth: '200px' }}>
              <Select
                isClearable
                isDisabled={disabled}
                options={availableUsers
                  .filter(user => !authors.find(a => a.user_id === user._id))
                  .map(user => ({
                    label: `${user.author_name || user.display_name}${user.email ? ` (${user.email})` : ''}`,
                    value: user._id,
                    userData: user
                  }))}
                value={selectedUser}
                onChange={setSelectedUser}
                placeholder="Select User"
                classNamePrefix="dark-select"
              />
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowManualInput(true)}
              className="d-flex align-items-center justify-content-center"
              style={{ height: '40px', padding: '0 10px' }}
            >
              Manual
            </Button>
          </>
        ) : (
          <>
            <div style={{ flex: 2, minWidth: '200px' }}>
              <Form.Control
                type="text"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                placeholder="Enter author name"
              />
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setShowManualInput(false);
                setManualName('');
              }}
              className="d-flex align-items-center justify-content-center"
              style={{ height: '40px', padding: '0 10px' }}
            >
              Select User
            </Button>
          </>
        )}
        <div style={{ flex: 1, minWidth: '150px' }}>
          <Select
            isDisabled={disabled}
            options={CONTRIBUTION_ROLES}
            value={selectedRole}
            onChange={setSelectedRole}
            classNamePrefix="dark-select"
          />
        </div>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={handleAddAuthor}
          disabled={disabled || (!selectedUser && !manualName)}
          className="d-flex align-items-center justify-content-center"
          style={{ width: '40px', height: '40px', padding: 0 }}
        >
          <FaPlus />
        </Button>
      </div>

      {/* Authors List */}
      {authors.map((author, index) => {
        const roleColor = CONTRIBUTION_ROLES.find(r => r.value === author.role)?.color || 'secondary';
        
        return (
          <div key={index} className="mb-2 p-2 border rounded" 
               style={{ background: 'rgba(255,255,255,0.05)' }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="d-flex align-items-center">
                <FaUser className="me-2" style={{ opacity: 0.7 }} />
                <div>
                  <div className="d-flex align-items-center">
                    <strong className="me-2">{getDisplayName(author)}</strong>
                    <Badge bg={roleColor} className="me-1">
                      {author.role}
                    </Badge>
                    {!author.user_id && (
                      <Badge bg="info" className="me-1">Manual</Badge>
                    )}
                  </div>
                  {author.user_id && getUserDetails(author.user_id)?.email && (
                    <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                      {getUserDetails(author.user_id).email}
                    </div>
                  )}
                </div>
              </div>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeAuthor(index)}
                disabled={disabled}
                className="d-flex align-items-center justify-content-center"
                style={{ width: '32px', height: '32px', padding: 0 }}
              >
                <FaTrash />
              </Button>
            </div>

            <Form.Control
              type="text"
              placeholder="Contribution notes (e.g., 'Provided RGB mod documentation')"
              value={author.notes || ''}
              onChange={(e) => {
                const updatedAuthors = [...authors];
                updatedAuthors[index] = { ...author, notes: e.target.value };
                onChange(updatedAuthors);
              }}
              size="sm"
              className="flex-grow-1 mb-2"
            />
            <Form.Control
              type="url"
              placeholder="Attribution URL"
              value={author.url || ''}
              onChange={(e) => {
                const updatedAuthors = [...authors];
                updatedAuthors[index] = { ...author, url: e.target.value };
                onChange(updatedAuthors);
              }}
              size="sm"
            />
          </div>
        );
      })}
    </div>
  );
};

export default AuthorInput; 